import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  DragIndicator,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  ProjectTypeItem,
  getProjectTypeData,
  saveProjectTypeData,
  generateProjectTypeId,
} from '../data/projectTypeData';

export const ProjectTypeEditor: React.FC = () => {
  const [projectTypes, setProjectTypes] = useState<ProjectTypeItem[]>([]);
  const [editingItem, setEditingItem] = useState<{ groupIndex: number; itemIndex: number } | null>(null);
  const [newItemDialog, setNewItemDialog] = useState<number | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [editItemLabel, setEditItemLabel] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<number[]>([]);

  useEffect(() => {
    const initializeData = async () => {
      await loadProjectTypes();
    };
    initializeData();
  }, []);

  const loadProjectTypes = async () => {
    try {
      const data = await getProjectTypeData();
      setProjectTypes(data);
      // 기본적으로 모든 그룹을 확장
      setExpandedGroups(data.map((_, index) => index));
    } catch (error) {
      console.error('프로젝트 타입 데이터 로드 실패:', error);
    }
  };

  const handleSave = async () => {
    try {
      await saveProjectTypeData(projectTypes);
      // 커스텀 이벤트 발생시켜 다른 컴포넌트에서 변경사항 감지
      window.dispatchEvent(new Event('projectTypeDataChanged'));
      setSuccessMessage('프로젝트 유형이 성공적으로 저장되었습니다.');
      setShowSuccess(true);
    } catch (error) {
      console.error('프로젝트 타입 데이터 저장 실패:', error);
      setSuccessMessage('저장 중 오류가 발생했습니다.');
      setShowSuccess(true);
    }
  };







  const handleAddItem = (groupIndex: number) => {
    if (newItemLabel.trim()) {
      const updatedTypes = [...projectTypes];
      const newId = generateProjectTypeId(groupIndex, updatedTypes[groupIndex].children.length);
      const newItem = {
        id: newId,
        label: newItemLabel.trim(),
      };
      updatedTypes[groupIndex].children.push(newItem);
      setProjectTypes(updatedTypes);
      setNewItemLabel('');
      setNewItemDialog(null);
    }
  };

  const handleEditItem = (groupIndex: number, itemIndex: number) => {
    setEditingItem({ groupIndex, itemIndex });
    setEditItemLabel(projectTypes[groupIndex].children[itemIndex].label);
  };

  const handleSaveItem = () => {
    if (editingItem && editItemLabel.trim()) {
      const updatedTypes = [...projectTypes];
      updatedTypes[editingItem.groupIndex].children[editingItem.itemIndex].label = editItemLabel.trim();
      setProjectTypes(updatedTypes);
      setEditingItem(null);
      setEditItemLabel('');
    }
  };

  const handleDeleteItem = (groupIndex: number, itemIndex: number) => {
    if (window.confirm('이 항목을 삭제하시겠습니까?')) {
      const updatedTypes = [...projectTypes];
      updatedTypes[groupIndex].children.splice(itemIndex, 1);
      // ID 재생성
      updatedTypes[groupIndex].children = updatedTypes[groupIndex].children.map((item, idx) => ({
        ...item,
        id: generateProjectTypeId(groupIndex, idx),
      }));
      setProjectTypes(updatedTypes);
    }
  };

  const toggleGroupExpansion = (index: number) => {
    setExpandedGroups(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const moveGroup = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= projectTypes.length) return;
    
    const updatedTypes = [...projectTypes];
    const [movedGroup] = updatedTypes.splice(fromIndex, 1);
    updatedTypes.splice(toIndex, 0, movedGroup);
    setProjectTypes(updatedTypes);
  };

  const moveItem = (groupIndex: number, fromIndex: number, toIndex: number) => {
    const group = projectTypes[groupIndex];
    if (toIndex < 0 || toIndex >= group.children.length) return;

    const updatedTypes = [...projectTypes];
    const [movedItem] = updatedTypes[groupIndex].children.splice(fromIndex, 1);
    updatedTypes[groupIndex].children.splice(toIndex, 0, movedItem);
    
    // ID 재생성
    updatedTypes[groupIndex].children = updatedTypes[groupIndex].children.map((item, idx) => ({
      ...item,
      id: generateProjectTypeId(groupIndex, idx),
    }));
    
    setProjectTypes(updatedTypes);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          프로젝트 유형 관리
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<Save />}
          >
            저장
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {projectTypes.map((group, groupIndex) => (
          <Grid item xs={12} md={6} lg={4} key={groupIndex}>
            <Card sx={{ height: 'fit-content' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{group.title}</Typography>
                    <Chip 
                      label={`${group.children.length}개`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                }
                action={
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => toggleGroupExpansion(groupIndex)}
                    >
                      {expandedGroups.includes(groupIndex) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                }
                sx={{ pb: 1 }}
              />
              
              {expandedGroups.includes(groupIndex) && (
                <CardContent sx={{ pt: 0 }}>
                  <List dense>
                    {group.children.map((item, itemIndex) => (
                      <ListItem key={item.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            editingItem?.groupIndex === groupIndex && editingItem?.itemIndex === itemIndex ? (
                              <TextField
                                value={editItemLabel}
                                onChange={(e) => setEditItemLabel(e.target.value)}
                                size="small"
                                fullWidth
                                autoFocus
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveItem();
                                  }
                                }}
                              />
                            ) : (
                              item.label
                            )
                          }
                        />
                        <ListItemSecondaryAction>
                          {editingItem?.groupIndex === groupIndex && editingItem?.itemIndex === itemIndex ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={handleSaveItem}
                                color="primary"
                              >
                                <Save />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingItem(null);
                                  setEditItemLabel('');
                                }}
                              >
                                <Cancel />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditItem(groupIndex, itemIndex)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteItem(groupIndex, itemIndex)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setNewItemDialog(groupIndex)}
                  >
                    항목 추가
                  </Button>
                </CardContent>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>



      {/* 새 항목 추가 다이얼로그 */}
      <Dialog open={newItemDialog !== null} onClose={() => setNewItemDialog(null)}>
        <DialogTitle>새 항목 추가</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="항목 이름"
            fullWidth
            variant="outlined"
            value={newItemLabel}
            onChange={(e) => setNewItemLabel(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newItemDialog !== null) {
                handleAddItem(newItemDialog);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewItemDialog(null)}>취소</Button>
          <Button 
            onClick={() => newItemDialog !== null && handleAddItem(newItemDialog)} 
            variant="contained"
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 성공 메시지 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 